/**
 * `HistorialBody` — contenido del tab Historial de `/vecino/estado-cuenta`.
 *
 * Sprint 14 S14-Historial. Inicialmente fue una página standalone
 * (`/vecino/historial`); el usuario lo movió a tab dentro de "Mi cuenta"
 * porque conceptualmente es la misma vista de la información financiera
 * del vecino, solo que con scope "todo lo pagado" vs "qué tengo
 * pendiente hoy".
 *
 * Recibe el `loteId` como prop desde `EstadoCuenta`, que ya tiene el
 * selector multi-tenencia. Reusa los endpoints existentes:
 *   - `GET /lotes/{id}/estado-cuenta` → cuotas con pagos_aplicados.
 *   - `GET /lotes/{id}/ingresos-varios` → ingresos varios aprobados.
 *
 * Los items se normalizan a `MovimientoHistorial`, se ordenan por fecha
 * desc y se agrupan por año. Tabs internos permiten filtrar por tipo.
 */
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import { getEstadoCuenta } from '../../lib/api/lotes';
import {
  listarIngresosVariosDeLote,
  type IngresoVarioVecinoRead,
} from '../../lib/api/ingresos-varios';
import type { EstadoCuentaResponse } from '../../lib/api/types';
import { formatCLP } from '../../lib/format';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { EmptyState } from '../../components/ui/EmptyState';
import { InfoNote } from '../../components/ui/InfoNote';

const MESES_CORTO = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
];

function fechaAmigable(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, anio, mm, dd] = m;
  const idx = parseInt(mm, 10) - 1;
  return `${parseInt(dd, 10)} ${MESES_CORTO[idx] ?? mm} ${anio}`;
}

function periodoLegible(periodo: string): string {
  const m = /^(\d{4})-S(1|2)$/.exec(periodo);
  if (!m) return periodo;
  const [, anio, sem] = m;
  return sem === '1' ? `1er semestre ${anio}` : `2do semestre ${anio}`;
}

interface MovimientoHistorial {
  id: string;
  fecha: string; // YYYY-MM-DD
  tipo: 'cuota' | 'otro';
  descripcion: string;
  detalle: string | null;
  monto: number;
}

/**
 * Aplana las cuotas en items individuales (uno por pago_aplicado) y los
 * combina con los ingresos varios aprobados.
 */
function construirHistorial(
  estado: EstadoCuentaResponse | null,
  otros: IngresoVarioVecinoRead[],
): MovimientoHistorial[] {
  const items: MovimientoHistorial[] = [];

  if (estado) {
    for (const cuota of estado.cuotas) {
      for (const pago of cuota.pagos_aplicados) {
        items.push({
          id: `cuota-${cuota.cuota_id}-${pago.pago_id}`,
          fecha: pago.fecha_transferencia,
          tipo: 'cuota',
          descripcion: periodoLegible(cuota.periodo),
          detalle: pago.num_operacion ? `Op. ${pago.num_operacion}` : null,
          monto: pago.monto_aplicado,
        });
      }
    }
  }

  for (const iv of otros) {
    if (iv.estado !== 'aprobado') continue;
    items.push({
      id: `otro-${iv.id}`,
      fecha: iv.fecha,
      tipo: 'otro',
      descripcion: iv.descripcion,
      detalle: iv.categoria_nombre,
      monto: iv.monto,
    });
  }

  items.sort((a, b) => {
    const cmp = b.fecha.localeCompare(a.fecha);
    if (cmp !== 0) return cmp;
    return a.tipo.localeCompare(b.tipo);
  });

  return items;
}

interface GrupoAnio {
  anio: string;
  items: MovimientoHistorial[];
  total: number;
  totalCuotas: number;
  totalOtros: number;
}

function agruparPorAnio(items: MovimientoHistorial[]): GrupoAnio[] {
  const mapa = new Map<string, MovimientoHistorial[]>();
  for (const item of items) {
    const anio = item.fecha.slice(0, 4);
    const lista = mapa.get(anio) ?? [];
    lista.push(item);
    mapa.set(anio, lista);
  }
  const anios = Array.from(mapa.keys()).sort((a, b) => b.localeCompare(a));
  return anios.map((anio) => {
    const arr = mapa.get(anio)!;
    return {
      anio,
      items: arr,
      total: arr.reduce((s, x) => s + x.monto, 0),
      totalCuotas: arr
        .filter((x) => x.tipo === 'cuota')
        .reduce((s, x) => s + x.monto, 0),
      totalOtros: arr
        .filter((x) => x.tipo === 'otro')
        .reduce((s, x) => s + x.monto, 0),
    };
  });
}

interface HistorialBodyProps {
  loteId: string;
}

export function HistorialBody({ loteId }: HistorialBodyProps) {
  const [estado, setEstado] = useState<EstadoCuentaResponse | null>(null);
  const [otros, setOtros] = useState<IngresoVarioVecinoRead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  type Filtro = 'todos' | 'cuota' | 'otro';
  const [filtro, setFiltro] = useState<Filtro>('todos');

  useEffect(() => {
    if (!loteId) {
      setEstado(null);
      setOtros([]);
      return;
    }
    let cancelado = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getEstadoCuenta(loteId),
      listarIngresosVariosDeLote(loteId).catch(() => []),
    ])
      .then(([est, ivs]) => {
        if (cancelado) return;
        setEstado(est);
        setOtros(ivs);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `No pudimos cargar el historial (error ${err.status}).`
            : 'No pudimos cargar el historial. Intenta recargar.',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [loteId]);

  const movimientos = useMemo(
    () => construirHistorial(estado, otros),
    [estado, otros],
  );
  const movimientosFiltrados = useMemo(() => {
    if (filtro === 'todos') return movimientos;
    return movimientos.filter((m) => m.tipo === filtro);
  }, [movimientos, filtro]);
  const grupos = useMemo(
    () => agruparPorAnio(movimientosFiltrados),
    [movimientosFiltrados],
  );

  return (
    <div className="space-y-3">
      {/* Sub-filtros por tipo de movimiento. */}
      <div className="flex gap-1 -mt-2 -mb-1" role="tablist">
        {(
          [
            { id: 'todos' as Filtro, label: 'Todos' },
            { id: 'cuota' as Filtro, label: 'Cuotas' },
            { id: 'otro' as Filtro, label: 'Otros pagos' },
          ]
        ).map((f) => {
          const activo = filtro === f.id;
          return (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={activo}
              onClick={() => setFiltro(f.id)}
              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                activo
                  ? 'bg-orange/20 text-orange'
                  : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <Card>
          <p className="text-sm text-text-muted">Cargando historial…</p>
        </Card>
      )}

      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}

      {!loading && !error && grupos.length === 0 && (
        <EmptyState
          title="Aún no hay movimientos registrados"
          description="Acá aparecerán todos tus pagos (cuotas y otros) a medida que se vayan registrando."
        />
      )}

      {!loading &&
        !error &&
        grupos.map((g) => (
          <Card key={g.anio}>
            <header className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
              <h3 className="text-lg font-medium text-text-primary">
                {g.anio}
              </h3>
              <span className="text-xs text-text-muted">
                Total {formatCLP(g.total)}
                {g.totalCuotas > 0 && g.totalOtros > 0 && (
                  <>
                    {' · '}
                    <span className="text-text-secondary">
                      Cuotas {formatCLP(g.totalCuotas)} · Otros{' '}
                      {formatCLP(g.totalOtros)}
                    </span>
                  </>
                )}
              </span>
            </header>
            <ul className="space-y-2 border-t-[0.5px] border-white/[0.08] pt-3">
              {g.items.map((item) => (
                <li
                  key={item.id}
                  className="grid grid-cols-[auto_1fr_auto] gap-2 sm:gap-3 items-start text-xs sm:text-sm"
                >
                  <span className="text-text-muted whitespace-nowrap pt-0.5">
                    {fechaAmigable(item.fecha)}
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Pill
                        variant={item.tipo === 'cuota' ? 'success' : 'info'}
                      >
                        {item.tipo === 'cuota' ? 'Cuota' : 'Otro'}
                      </Pill>
                      <span className="text-text-primary truncate">
                        {item.descripcion}
                      </span>
                    </div>
                    {item.detalle && (
                      <div className="text-xs text-text-muted mt-0.5">
                        {item.detalle}
                      </div>
                    )}
                  </div>
                  <span className="font-medium text-text-primary whitespace-nowrap">
                    {formatCLP(item.monto)}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        ))}

      {!loading && !error && grupos.length > 0 && (
        <InfoNote>
          "Cuota" = pago aplicado a una cuota semestral (puede haber varios
          por semestre si pagaste en abonos). "Otro" = ingresos varios
          aprobados como multas, ventas de controles/llaveros, aportes. Los
          pagos pendientes de revisión no aparecen acá hasta que la
          Comisión los confirma.
        </InfoNote>
      )}
    </div>
  );
}
